import { CheckboxInput, Modal } from '@app/components'
import {
  isScholarshipConfirmResult,
  showMessagesNotifications,
  type StreamConflict,
  useConfirmScholarship
} from '@app/lib'
import { type SugarEnrollmentItem, type SugarScholarshipConfirmResult } from '@app/types'
import { Alert, Button, Group, Stack, Text } from '@mantine/core'
import { useIsomorphicEffect } from '@mantine/hooks'
import { IconAlertTriangle } from '@tabler/icons-react'
import { map, some } from 'lodash'
import { useRef, useState, type FC } from 'react'

export interface ScholarshipConfirmModalProps {
  conflicts: StreamConflict[]
  // True when the row carries a funding answer the provider typed but has not
  // saved. Separate from `conflicts`, which only ever names CCCAP and Head Start
  // -- the two streams that contradict the attestation -- and only when the
  // answer is Yes. Confirming discards every unsaved answer on the row, not just
  // those two (MIQ-2702).
  hasUnsavedFunding: boolean
  item: SugarEnrollmentItem | null
  onClose: () => void
  onConfirmed: (result: SugarScholarshipConfirmResult) => void
}

export const ScholarshipConfirmModal: FC<ScholarshipConfirmModalProps> = ({
  conflicts,
  hasUnsavedFunding,
  item,
  onClose,
  onConfirmed
}) => {
  const { confirmScholarship, isConfirmingScholarship } = useConfirmScholarship()
  const [familyChargedZero, setFamilyChargedZero] = useState(false)
  const [noOtherFundingExceptUpk, setNoOtherFundingExceptUpk] = useState(false)
  const [fundingNotGuaranteed, setFundingNotGuaranteed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const opened = Boolean(item)
  // The previous open state, tracked explicitly, because nothing else here
  // changes when the same row is reopened. `item` is items[itemIdx] straight out
  // of the query cache: a failed confirm patches nothing, so a second Confirm on
  // that row passes the identical object and an effect keyed on `item` -- or on
  // item.enrollment.id, which is exactly as stable -- never re-runs. The
  // component does not unmount either; keepMounted={false} unmounts the modal's
  // content, not the state above.
  const wasOpenedRef = useRef(false)

  // Reset on the closed -> open transition, never on close. A failed submit
  // leaves the modal open, so no transition occurs and both boxes stay checked
  // for a one-click retry -- while every actual open starts clean. Two legal
  // attestations must never be pre-ticked. A layout effect so the reset lands
  // before paint rather than flashing the previous ticks for a frame.
  useIsomorphicEffect(() => {
    if (opened && !wasOpenedRef.current) {
      setFamilyChargedZero(false)
      setNoOtherFundingExceptUpk(false)
      setFundingNotGuaranteed(false)
      setError(null)
    }

    wasOpenedRef.current = opened
  }, [opened])

  const onSubmit = async () => {
    if (!item) return

    setError(null)

    try {
      const response = await confirmScholarship({
        enrollmentId: item.enrollment.id,
        familyChargedZero,
        noOtherFundingExceptUpk
      })

      // Everything the server said, verbatim and in full. On the success path
      // this modal closes, so a notification is the only place a message can
      // live at all -- before this, the confirm endpoint's messages were parsed
      // for an error string and otherwise dropped.
      showMessagesNotifications(response.messages)

      if (response.success && isScholarshipConfirmResult(response.data)) {
        onConfirmed(response.data)
        onClose()

        return
      }

      // The rejection reason, but only when it did not already go out as a
      // notification just above. MIQ-2464+ endpoints carry it in the `error`
      // envelope while older ones send a message of type 'error', so surfacing
      // both unconditionally would say the same sentence twice -- once inline,
      // once as a toast.
      if (!some(response.messages, ['type', 'error'])) {
        setError(response.error?.message ?? 'Unable to confirm this scholarship. Please try again.')
      }
    } catch (e) {
      // Only a transport failure reaches here now: a business rejection
      // resolves with success: false and is handled above. No else branch,
      // because axios rejects with AxiosError, which extends Error.
      if (e instanceof Error) {
        setError(e.message)
      }
    }
  }

  return (
    // Every dismissal route is shut while the POST is in flight, matching the
    // already-disabled Cancel button. Closing mid-request hides the modal that
    // the failure message is about to be written into, so a rejected confirm
    // would leave no feedback at all; and on the success path it frees the
    // provider to switch site or program year before the row the result belongs
    // to has been rewritten.
    //
    // Escape and click-outside are already off for every modal in the app
    // (MantineProvider theme defaults), but they are restated here rather than
    // inherited: two legal attestations and an irreversible POST should not
    // depend on a global default staying put. The header close button is the
    // route that was genuinely still open -- the LoadingOverlay covers the body
    // only, the header sits outside it, so the X stayed clickable mid-request.
    <Modal
      closeButtonProps={{ disabled: isConfirmingScholarship }}
      closeOnClickOutside={false}
      closeOnEscape={false}
      isLoading={isConfirmingScholarship}
      onClose={onClose}
      opened={opened}
      size={520}
      title='Confirm Scholarship'
    >
      <Stack spacing='md'>
        <Text
          color='dimmed'
          size='sm'
        >
          {item?.contact.name}
        </Text>

        {map(conflicts, (conflict) => (
          <Alert
            color='orange'
            icon={<IconAlertTriangle size={18} />}
            key={conflict.stream}
            radius='md'
          >
            <Text size='sm'>
              {conflict.stored
                ? `This student is recorded as receiving ${conflict.stream}, so this confirmation will be rejected. Change the ${conflict.stream} answer and save it first.`
                : `You have unsaved ${conflict.stream} = Yes for this student. A child receiving ${conflict.stream} cannot hold an active DPP scholarship. Confirming will discard that answer.`}
            </Text>
          </Alert>
        ))}

        {/* MIQ-2702: confirming resets the row to the server's values, and every
            funding box on a confirmed row is then disabled. Before the lock
            covered UPK and SFP, a discarded answer there was a retype away; now
            it can only be restored by DPP. The two alerts can appear together --
            an unsaved CCCAP = Yes is both a conflict and an unsaved answer --
            which is why this one names no specific stream. */}
        {hasUnsavedFunding && (
          <Alert
            color='orange'
            icon={<IconAlertTriangle size={18} />}
            radius='md'
          >
            <Text size='sm'>
              You have unsaved funding answers for this student. Confirming discards them and locks these fields, so
              save them first if you want them kept.
            </Text>
          </Alert>
        )}

        <Stack spacing={4}>
          <CheckboxInput
            checked={familyChargedZero}
            label='If the student is selected for a DPP Scholarship award, I confirm they will be charged $0 in tuition'
            onChange={(event) => setFamilyChargedZero(event.currentTarget.checked)}
          />
          <CheckboxInput
            checked={noOtherFundingExceptUpk}
            label='I confirm this child is not receiving any funding from CCCAP or Head Start'
            onChange={(event) => setNoOtherFundingExceptUpk(event.currentTarget.checked)}
          />
          {/* UI attestation only: the confirm endpoint has no field for this, so it
              gates submission but is not sent. */}
          <CheckboxInput
            checked={fundingNotGuaranteed}
            label='I understand scholarship funding is limited, and that confirming this information does not guarantee a scholarship will be awarded to this student.'
            onChange={(event) => setFundingNotGuaranteed(event.currentTarget.checked)}
          />
        </Stack>

        {error && (
          <Alert
            color='red'
            radius='md'
          >
            <Text size='sm'>{error}</Text>
          </Alert>
        )}

        <Group position='right'>
          <Button
            disabled={isConfirmingScholarship}
            onClick={onClose}
            variant='subtle'
          >
            Cancel
          </Button>
          <Button
            disabled={!familyChargedZero || !noOtherFundingExceptUpk || !fundingNotGuaranteed}
            loading={isConfirmingScholarship}
            onClick={onSubmit}
          >
            Submit Confirmation
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
